import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useToast } from '@/hooks/use-toast'
import { apiFetch } from '@/utils'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

interface ChatState {
  messages: Message[]
  isOpen: boolean
  isTyping: boolean
  error: string | null
  lastUserMessage: string | null
  addMessage: (role: Message['role'], content: string) => void
  updateLastMessage: (content: string) => void
  setIsOpen: (open: boolean) => void
  setIsTyping: (typing: boolean) => void
  setError: (error: string | null) => void
  setLastUserMessage: (message: string | null) => void
  clearMessages: () => void
}

const MAX_MESSAGES = 50
const MAX_RETRIES = 3
const RETRY_DELAY = 1000
const CACHE_DURATION = 30 * 60 * 1000 // 30 minutes
const STREAM_TIMEOUT = 30000 // 30 seconds

const initialState = {
  messages: [],
  isOpen: false,
  isTyping: false,
  error: null,
  lastUserMessage: null
}

const useChatStore = create<ChatState>()(
  persist(
    (set) => ({
      ...initialState,
      addMessage: (role, content) =>
        set((state) => {
          const newMessages = [
            ...state.messages.slice(-MAX_MESSAGES + 1),
            {
              id: crypto.randomUUID(),
              role,
              content,
              timestamp: new Date()
            }
          ]

          const now = Date.now()
          const validMessages = newMessages.filter(
            (msg) => now - new Date(msg.timestamp).getTime() < CACHE_DURATION
          )

          return {
            messages: validMessages,
            error: null,
            lastUserMessage: role === 'user' ? content : state.lastUserMessage
          }
        }),
      updateLastMessage: (content) =>
        set((state) => {
          const messages = [...state.messages]
          const lastMessage = messages[messages.length - 1]

          if (lastMessage && lastMessage.role === 'assistant') {
            messages[messages.length - 1] = {
              ...lastMessage,
              content,
              timestamp: new Date()
            }
          }

          return { messages }
        }),
      setIsOpen: (open) => set({ isOpen: open, error: null }),
      setIsTyping: (typing) => set({ isTyping: typing }),
      setError: (error) => set({ error, isTyping: false }),
      setLastUserMessage: (message) => set({ lastUserMessage: message }),
      clearMessages: () =>
        set({
          messages: [],
          error: null,
          lastUserMessage: null
        })
    }),
    {
      name: 'chat-storage',
      partialize: (state) => ({
        messages: state.messages.map((msg) => ({
          ...msg,
          timestamp: msg.timestamp.toISOString() // Convert Date to string for storage
        }))
      }),
      version: 1,
      onRehydrateStorage: () => (state) => {
        if (state?.messages) {
          // Convert ISO strings back to Date objects
          state.messages = state.messages.map((msg) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }))
        }
      }
    }
  )
)

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export function useChat() {
  const { toast } = useToast()
  const store = useChatStore()

  const handleError = (error: Error, retryCount: number) => {
    const errorMessage = error.message || 'Failed to send message'
    store.setError(errorMessage)

    if (retryCount < MAX_RETRIES) {
      toast({
        title: 'Retrying...',
        description: `Attempt ${retryCount + 1} of ${MAX_RETRIES}`,
        duration: 3000
      })
    } else {
      toast({
        title: 'Chat Error',
        description: errorMessage,
        variant: 'destructive'
      })
    }
  }

  const sendMessage = async (
    content: string,
    retryCount = 0
  ): Promise<void> => {
    if (!content.trim()) return

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), STREAM_TIMEOUT)

    try {
      store.setError(null)
      store.setLastUserMessage(content)
      store.addMessage('user', content)
      store.setIsTyping(true)

      const recentMessages = store.messages.slice(-5).map((msg) => ({
        role: msg.role,
        content: msg.content
      }))

      const response = await apiFetch('/v1/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: content,
          context: recentMessages
        }),
        signal: controller.signal
      })

      if (!response.ok || !response.body) {
        throw new Error('Failed to connect to chat service')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let accumulatedResponse = ''

      try {
        while (true) {
          const { value, done } = await reader.read()
          if (done) break

          const chunk = decoder.decode(value)
          const lines = chunk.split('\n')

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(5))

                if (data.error) {
                  throw new Error(data.error)
                }

                if (data.chunk && data.isPartial) {
                  accumulatedResponse += data.chunk
                  store.updateLastMessage(accumulatedResponse)
                } else if (data.message && data.complete) {
                  store.addMessage('assistant', data.message)
                  break
                }
              } catch (e) {
                console.error('Error parsing SSE message:', e)
              }
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          handleError(new Error('Request timed out'), retryCount)
        } else {
          handleError(error, retryCount)
        }

        if (retryCount < MAX_RETRIES) {
          await sleep(RETRY_DELAY * Math.pow(2, retryCount))
          return sendMessage(content, retryCount + 1)
        }
      }
    } finally {
      clearTimeout(timeoutId)
      controller.abort()
      store.setIsTyping(false)
    }
  }

  return {
    messages: store.messages,
    isOpen: store.isOpen,
    isTyping: store.isTyping,
    error: store.error,
    sendMessage,
    setIsOpen: store.setIsOpen,
    clearMessages: store.clearMessages
  }
}
